import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Lock, Plus, Search, Swords, Users } from 'lucide-react';
import { MAX_PLAYERS, MIN_PLAYERS, createRoomBody, defaultSettings, type CreatedRoomDto, type GameKind, type RoomListItem } from '@mafia/core';
import { useAuth } from '@/store/auth';
import { KindBadge, KindTabs } from '@/components/Kind';
import { ApiError, api } from '@/lib/api';
import { useFormSubmit } from '@/lib/forms';
import { Alert, Badge, Button, Card, Empty, Field, Input, Modal, Spinner } from '@/components/ui';

export function RoomsPage() {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const rooms = useQuery({
    queryKey: ['rooms'],
    queryFn: () => api<RoomListItem[]>('/api/rooms'),
    refetchInterval: 5000,
  });

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:py-10">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Комнаты</h1>
          <p className="mt-1 text-sm text-zinc-500">Общайтесь голосом в Discord или за столом — сайт ведёт игру.</p>
        </div>
        <div className="flex w-full flex-wrap gap-2 sm:w-auto">
          <JoinByCode />
          <Button onClick={() => setCreating(true)} className="flex-1 sm:flex-none">
            <Plus className="h-4 w-4" /> Создать комнату
          </Button>
        </div>
      </div>

      {rooms.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : rooms.isError ? (
        <Alert>Не удалось загрузить комнаты</Alert>
      ) : rooms.data!.length === 0 ? (
        <Card>
          <Empty icon="🕯️" title="Пока ни одной комнаты">
            Создайте свою и пригласите друзей по коду.
          </Empty>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {rooms.data!.map((r) => (
            <button
              key={r.id}
              onClick={() => navigate(`/room/${r.id}`)}
              disabled={r.inGame}
              className="card group flex flex-col gap-3 p-4 text-left transition hover:border-gold-400/30 hover:bg-ink-850 disabled:opacity-60"
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="line-clamp-1 font-medium text-zinc-100 group-hover:text-white">{r.name}</h3>
                {r.isPrivate && <Lock className="h-4 w-4 shrink-0 text-gold-400" aria-label="Приватная" />}
              </div>
              <div className="flex items-center justify-between text-sm text-zinc-400">
                <span>Хост: {r.hostNickname}</span>
                <span className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  {r.members}/{r.maxPlayers}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <KindBadge kind={r.kind} />
                {r.inGame ? (
                  <Badge tone="red">
                    <Swords className="h-3 w-3" /> Идёт игра
                  </Badge>
                ) : r.members >= r.maxPlayers ? (
                  <Badge>Заполнена</Badge>
                ) : (
                  <Badge tone="green">Набор игроков</Badge>
                )}
                <span className="ml-auto font-mono text-xs text-zinc-600">{r.code}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <CreateRoomModal open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

function JoinByCode() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!code.trim()) return;
    setPending(true);
    setError(null);
    try {
      const room = await api<{ id: string }>(`/api/rooms/by-code/${encodeURIComponent(code.trim())}`);
      navigate(`/room/${room.id}`);
    } catch (err) {
      setError(err instanceof ApiError && err.status === 404 ? 'Нет такой комнаты' : 'Ошибка');
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-1 gap-2 sm:flex-none">
      <div className="relative flex-1">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Код комнаты"
          maxLength={8}
          className="font-mono uppercase sm:w-40"
          error={error ?? undefined}
          aria-label="Код комнаты"
          title={error ?? undefined}
        />
        {error && <span className="absolute -bottom-5 left-1 text-xs text-blood-400">{error}</span>}
      </div>
      <Button variant="secondary" type="submit" loading={pending} aria-label="Найти">
        <Search className="h-4 w-4" />
      </Button>
    </form>
  );
}

function CreateRoomModal({ open, onClose }: { open: boolean; onClose(): void }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [isPrivate, setPrivate] = useState(false);
  const [password, setPassword] = useState('');
  const [maxPlayers, setMax] = useState(10);
  const [kind, setKind] = useState<GameKind>('AMATEUR');
  const isAdmin = useAuth((s) => s.user?.role === 'ADMIN');
  const { errors, formError, pending, handle } = useFormSubmit(createRoomBody, async (body) => {
    const room = await api<CreatedRoomDto>('/api/rooms', { method: 'POST', body });
    navigate(`/room/${room.id}`, { state: { password: body.password } });
  });

  return (
    <Modal open={open} onClose={onClose} title="Новая комната">
      <form
        className="space-y-4"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void handle({
            name,
            isPrivate,
            password: isPrivate ? password : undefined,
            maxPlayers,
            settings: defaultSettings(maxPlayers),
            kind: isAdmin ? kind : 'AMATEUR',
          });
        }}
      >
        {formError && <Alert>{formError}</Alert>}
        {isAdmin && (
          <div>
            <span className="label">Вид игры</span>
            <KindTabs value={kind} onChange={(k) => setKind(k)} labels="single" />
            <p className="mt-1.5 text-xs text-zinc-500">
              {kind === 'TOURNAMENT'
                ? 'Турнирные столы создают только администраторы, баллы идут в турнирный рейтинг.'
                : 'Любительский стол: баллы идут в любительский рейтинг.'}
            </p>
          </div>
        )}
        <Field label="Название" error={errors.name}>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Пятничная мафия" autoFocus maxLength={48} />
        </Field>
        <Field label={`Игроков: до ${maxPlayers}`} hint="Роли и таймеры настроите в лобби">
          <input
            type="range"
            min={MIN_PLAYERS}
            max={MAX_PLAYERS}
            value={maxPlayers}
            onChange={(e) => setMax(Number(e.target.value))}
            className="w-full accent-blood-500"
          />
        </Field>
        <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-white/5 bg-ink-850 px-3.5 py-3">
          <input type="checkbox" checked={isPrivate} onChange={(e) => setPrivate(e.target.checked)} className="h-4 w-4 accent-gold-400" />
          <span className="text-sm">
            Приватная комната <span className="text-zinc-500">— вход по паролю</span>
          </span>
        </label>
        {isPrivate && (
          <Field label="Пароль комнаты" error={errors.password}>
            <Input value={password} onChange={(e) => setPassword(e.target.value)} maxLength={64} />
          </Field>
        )}
        <Button type="submit" className="w-full" loading={pending}>
          Создать и войти
        </Button>
      </form>
    </Modal>
  );
}
